import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16 } from "styled-components";
import React from "react";
import { m, useScroll, useTransform, useMotionTemplate, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { takeFirst } from "~/features/app/utils";
import { Headline } from "~/features/ui/headline";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { isLinkValid } from "~/features/ui/link/utils";
const INTRO_VH = 350;
const SLIDE_VH = 150;
const TRANSLATE_Y = 200;
const DWELL_ZONE = 0.2;
const sectionStyle = /*#__PURE__*/_css(["", " height:100vh;height:100dvh;"], {
  "position": "sticky",
  "top": "0",
  "width": "100%",
  "overflow": "hidden",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
});
const maskStyle = /*#__PURE__*/_css(["", " will-change:clip-path;"], {
  "position": "absolute",
  "inset": "0",
  "zIndex": "20",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
});
const imageStyle = /*#__PURE__*/_css(["", " will-change:transform,filter;"], {
  "position": "absolute",
  "inset": "0",
  "height": "100%",
  "width": "100%"
});
const contentTransition = {
  duration: 0.6,
  ease: [0.125, 0.425, 0.27, 1] as const
};
export const rgbBgVideoProps = {
  fit: "cover" as const,
  rounded: false,
  autoPlay: true,
  muted: true,
  loop: true
};
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function RgbMedia({
  isActive,
  children
}: {
  isActive: boolean;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const findVideo = React.useCallback(() => (ref.current?.querySelector("mux-player") ?? ref.current?.querySelector("video")) as (HTMLVideoElement & {
    play(): Promise<void>;
    pause(): void;
  }) | null, []);
  React.useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        const target = findVideo();
        if (!target) return;
        target.currentTime = 0;
        target.play?.()?.catch(() => {});
      }, 100);
      return () => clearTimeout(timer);
    }

    // Inactive: pause immediately and poll briefly to catch delayed autoplay
    const pause = () => {
      const target = findVideo();
      if (target && !target.paused) {
        target.pause();
        target.currentTime = 0;
      }
    };
    pause();
    const interval = setInterval(pause, 250);
    const stop = setTimeout(() => clearInterval(interval), 3000);
    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [isActive, findVideo]);
  return <div ref={ref} style={{
    display: "contents"
  }}>
      {children}
    </div>;
}
export type RgbBlockViewSlide = {
  headline?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  media?: React.ReactNode;
  text?: React.ReactNode;
};
export type RgbBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getSlideKey?: (slide: RgbBlockViewSlide, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  media?: React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  slides?: RgbBlockViewSlide[] | null;
  text?: React.ReactNode;
};
export function RgbBlockView({
  anchor,
  getSlideKey,
  hasTopDivider = true,
  headline,
  media,
  sectionProps,
  slides: rawSlides = [],
  text
}: RgbBlockViewProps) {
  const slides = rawSlides ?? [];
  const slidesCount = slides.length;
  const hasSlides = slidesCount > 0;
  const totalVh = INTRO_VH + (hasSlides ? slidesCount * SLIDE_VH : 0);
  const introEnd = INTRO_VH / totalVh;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Scale original intro thresholds to the proportion of the intro within the total container
  const p = (v: number) => v * introEnd;

  // Phase 1 (mask grows from center revealing image)
  const inset = useTransform(scrollYProgress, [p(0.15), p(0.55)], [50, 0]);
  const clipPath = useMotionTemplate`inset(${inset}%)`;
  const imageScale = useTransform(scrollYProgress, [p(0.15), p(0.55)], [1.2, 1.2]);

  // Phase 2 (blur reduces to 0)
  const blur = useTransform(scrollYProgress, [p(0.55), p(0.75)], [256, 0]);
  const blurFilter = useMotionTemplate`blur(${blur}px)`;

  // Phase 3 (content fades in) + scroll-driven slide transitions
  const [continuousIdx, setContinuousIdx] = React.useState(0);
  const [showContent, setShowContent] = React.useState(false);
  const [maskOpen, setMaskOpen] = React.useState(false);
  useMotionValueEvent(scrollYProgress, "change", v => {
    setMaskOpen(v >= p(0.55));
    if (v < p(0.85)) {
      setShowContent(false);
      setContinuousIdx(0);
      return;
    }
    setShowContent(true);
    if (!hasSlides || v <= introEnd) {
      setContinuousIdx(0);
      return;
    }
    const idx = (v - introEnd) / (1 - introEnd) * slidesCount;
    setContinuousIdx(Math.min(idx, slidesCount));
  });

  // Remap continuousIdx so it pauses at each integer (dwell zone)
  const remapped = React.useMemo(() => {
    const n = Math.floor(continuousIdx);
    const frac = continuousIdx - n;
    if (frac <= DWELL_ZONE) return n;
    if (frac >= 1 - DWELL_ZONE) return n + 1;
    return n + (frac - DWELL_ZONE) / (1 - 2 * DWELL_ZONE);
  }, [continuousIdx]);
  const getItemTransform = (itemIndex: number) => {
    const delta = Math.max(-1, Math.min(1, remapped - itemIndex));
    return {
      y: delta * -TRANSLATE_Y,
      opacity: Math.max(0, 1 - Math.abs(delta))
    };
  };

  // Discrete index for media and content crossfade (-1 = parent, 0+ = slide index)
  const activeMediaSlide = hasSlides ? Math.min(Math.round(remapped), slidesCount) - 1 : -1;
  const currentSlide = activeMediaSlide >= 0 ? slides[activeMediaSlide] : null;
  const activeText = currentSlide?.text ?? text;
  const activeLink = currentSlide ? takeFirst(currentSlide.link) : null;
  return <_StyledDiv ref={containerRef} style={{
    height: `${totalVh}vh`
  }} $_css2={[{
    "position": "relative"
  }]}>
      <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css3={[sectionStyle, hasTopDivider && {
      "borderTopWidth": "1px",
      "borderColor": "var(--stroke-primary)"
    }]}>
        <_StyledMDiv style={{
        clipPath
      }} $_css4={[maskStyle]}>
          {/* Headlines */}
          <_StyledDiv2>
            {/* Parent headline — always visible (mask clips it), scroll-driven exit when slides begin */}
            {headline && <_StyledDiv3 style={{
            transform: `translateY(${getItemTransform(0).y}px)`,
            opacity: getItemTransform(0).opacity,
            willChange: "transform, opacity"
          }}>
                <_StyledHeadline type="h2" text={headline} />
              </_StyledDiv3>}

            {/* Child headlines — scroll-driven vertical transition */}
            {showContent && slides.map((slide, i) => {
            const {
              y,
              opacity
            } = getItemTransform(i + 1);
            return slide.headline ? <_StyledDiv4 key={getSlideKey?.(slide, i) ?? i} style={{
              transform: `translateY(${y}px)`,
              opacity,
              willChange: "transform, opacity"
            }}>
                    <_StyledHeadline2 type="h2" text={slide.headline} />
                  </_StyledDiv4> : null;
          })}
          </_StyledDiv2>

          {/* Background images — all stacked, crossfade via opacity */}
          <_StyledMDiv2 style={{
          scale: imageScale,
          filter: blurFilter
        }} $_css5={[imageStyle]}>
            {media && <_StyledMDiv3 animate={{
            opacity: activeMediaSlide < 0 ? 1 : 0
          }} transition={contentTransition}>
                <RgbMedia isActive={activeMediaSlide < 0}>{media}</RgbMedia>
              </_StyledMDiv3>}

            {slides.map((slide, index) => {
            if (!slide.media) return null;
            return <_StyledMDiv4 key={getSlideKey?.(slide, index) ?? index} animate={{
              opacity: activeMediaSlide === index ? 1 : 0
            }} transition={contentTransition}>
                  <RgbMedia isActive={activeMediaSlide === index}>{slide.media}</RgbMedia>
                </_StyledMDiv4>;
          })}
          </_StyledMDiv2>
        </_StyledMDiv>

        {/* Headline behind mask (black text, only visible during intro before mask opens) */}
        <_StyledDiv5>
          {headline && !maskOpen && <_StyledDiv6>
              <_StyledHeadline3 type="h2" text={headline} />
            </_StyledDiv6>}
        </_StyledDiv5>

        {/* Bottom content: description + link */}
        <AnimatePresence mode="wait">
          {showContent && (activeText || isLinkValid(activeLink)) && <_StyledMDiv5 key={activeMediaSlide} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 20
        }} transition={contentTransition}>
              {activeText}
              {isLinkValid(activeLink) && <_StyledDiv7>
                  <Button as={Link} {...activeLink as PlainLink} variant="underline" />
                </_StyledDiv7>}
            </_StyledMDiv5>}
        </AnimatePresence>
      </_StyledSection>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-8ffy5g-0"
})`${p => p.$_css2}`;
var _StyledSection = _styled2("section").withConfig({
  componentId: "sc-sf-8ffy5g-1"
})`${p => p.$_css3}`;
var _StyledMDiv = _styled3(m.div).withConfig({
  componentId: "sc-sf-8ffy5g-2"
})`${p => p.$_css4}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-8ffy5g-3"
})({
  "position": "absolute",
  "inset": "0",
  "zIndex": "10",
  "overflow": "hidden",
  "textAlign": "center",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-8ffy5g-4"
})({
  "position": "absolute",
  "inset": "0",
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "center"
});
var _StyledHeadline = _styled6(Headline).withConfig({
  componentId: "sc-sf-8ffy5g-5"
})({
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-8ffy5g-6"
})({
  "position": "absolute",
  "inset": "0",
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "center"
});
var _StyledHeadline2 = _styled8(Headline).withConfig({
  componentId: "sc-sf-8ffy5g-7"
})({
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledMDiv2 = _styled9(m.div).withConfig({
  componentId: "sc-sf-8ffy5g-8"
})`${p => p.$_css5}`;
var _StyledMDiv3 = _styled10(m.div).withConfig({
  componentId: "sc-sf-8ffy5g-9"
})({
  "position": "absolute",
  "inset": "0"
});
var _StyledMDiv4 = _styled11(m.div).withConfig({
  componentId: "sc-sf-8ffy5g-10"
})({
  "position": "absolute",
  "inset": "0"
});
var _StyledDiv5 = _styled12("div").withConfig({
  componentId: "sc-sf-8ffy5g-11"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "grid",
  "height": "100%",
  "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
  "alignItems": "flex-end",
  "justifyContent": "flex-end",
  "padding": "var(--spacing-lg)",
  "@media (min-width: 1024px)": {
    "padding": "var(--spacing-xl)"
  }
});
var _StyledDiv6 = _styled13("div").withConfig({
  componentId: "sc-sf-8ffy5g-12"
})({
  "position": "absolute",
  "inset": "0",
  "zIndex": "10",
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "center",
  "textAlign": "center",
  "--tw-text-opacity": "1",
  "color": "rgb(0 7 4 / var(--tw-text-opacity))"
});
var _StyledHeadline3 = _styled14(Headline).withConfig({
  componentId: "sc-sf-8ffy5g-13"
})({
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledMDiv5 = _styled15(m.div).withConfig({
  componentId: "sc-sf-8ffy5g-14"
})({
  "position": "absolute",
  "bottom": "var(--spacing-md)",
  "left": "var(--spacing-md)",
  "zIndex": "20",
  "width": "34rem",
  "maxWidth": "100%",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "bottom": "var(--spacing-xl)",
    "left": "auto",
    "right": "var(--spacing-xl)",
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv7 = /*#__PURE__*/_styled16("div").withConfig({
  displayName: "rgb___StyledDiv7",
  componentId: "sc-4kqnxs-0"
})({
  "marginTop": "var(--spacing-md)"
});